import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { useAgentStore } from './store/agentStore';
import { useAgentNotifications } from './hooks/useSocket';

// Pages
import AgentLoginPage from './pages/auth/AgentLoginPage';
import AgentDashboardPage from './pages/dashboard/AgentDashboardPage';
import CashInPage from './pages/operations/CashInPage';
import CashOutPage from './pages/operations/CashOutPage';
import TransactionListPage from './pages/transactions/TransactionListPage';
import TransactionDetailPage from './pages/transactions/TransactionDetailPage';
import CommissionsPage from './pages/commissions/CommissionsPage';
import DailyReportsPage from './pages/reports/DailyReportsPage';
import PerformancePage from './pages/reports/PerformancePage';
import AgentProfilePage from './pages/profile/AgentProfilePage';
import FloatRechargePage from './pages/float/FloatRechargePage';
import FloatRechargeAdminPage from './pages/float/FloatRechargeAdminPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000,
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAgentStore((state) => state.isAuthenticated);
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

/** Mounts the Socket.IO connection and global notification handlers */
function SocketManager() {
  useAgentNotifications();
  return null;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SocketManager />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<AgentLoginPage />} />

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AgentDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cash-in"
            element={
              <ProtectedRoute>
                <CashInPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cash-out"
            element={
              <ProtectedRoute>
                <CashOutPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/transactions"
            element={
              <ProtectedRoute>
                <TransactionListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/transactions/:id"
            element={
              <ProtectedRoute>
                <TransactionDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/commissions"
            element={
              <ProtectedRoute>
                <CommissionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <DailyReportsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/performance"
            element={
              <ProtectedRoute>
                <PerformancePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <AgentProfilePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/float/request"
            element={
              <ProtectedRoute>
                <FloatRechargePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/float/requests"
            element={
              <ProtectedRoute>
                <FloatRechargeAdminPage />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>

      <Toaster position="top-center" />
    </QueryClientProvider>
  );
}

export default App;
